// This file is part of MinIO Console Server
// Copyright (c) 2022 MinIO, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU Affero General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU Affero General Public License for more details.
//
// You should have received a copy of the GNU Affero General Public License
// along with this program.  If not, see <http://www.gnu.org/licenses/>.

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SessionResponse } from "../../api/consoleApi";
import { SessionCallStates } from "./consoleSlice.types";
import { fetchConsoleSession } from "./sessionThunk";
import { AppState } from "app/store";
import { findUrlParts } from "../../api/api.utils";
import { LoadingStatus } from "../../common/types";

export interface ConsoleState {
  sessionDir: { [siteId: string]: { [resourceId: string]: SessionResponse } };
  sessionStatusDir: {
    [siteId: string]: { [resourceId: string]: LoadingStatus };
  };
  sessionLoadingState: SessionCallStates;
}

const initialState: ConsoleState = {
  sessionDir: {},
  sessionStatusDir: {},
  sessionLoadingState: SessionCallStates.Initial,
};

export const consoleSlice = createSlice({
  name: "console",
  initialState,
  reducers: {
    setSessionLoadingState: (
      state,
      action: PayloadAction<SessionCallStates>,
    ) => {
      state.sessionLoadingState = action.payload;
    },
    saveSessionResponse: (
      state,
      action: PayloadAction<{
        siteId: string;
        resourceId: string;
        session: SessionResponse;
      }>,
    ) => {
      if (!state.sessionDir.hasOwnProperty(action.payload.siteId)) {
        state.sessionDir[action.payload.siteId] = {};
      }
      state.sessionDir[action.payload.siteId][action.payload.resourceId] =
        action.payload.session;
    },
    resetSession: (state) => {
      state.sessionDir = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConsoleSession.pending, (state, action) => {
        if (!state.sessionStatusDir.hasOwnProperty(action.meta.arg.siteId)) {
          state.sessionStatusDir[action.meta.arg.siteId] = {};
        }
        state.sessionStatusDir[action.meta.arg.siteId][
          action.meta.arg.resourceId
        ] = "loading";
        state.sessionLoadingState = SessionCallStates.Loading;
      })
      .addCase(fetchConsoleSession.fulfilled, (state, action) => {
        if (!state.sessionStatusDir.hasOwnProperty(action.meta.arg.siteId)) {
          state.sessionStatusDir[action.meta.arg.siteId] = {};
        }
        state.sessionStatusDir[action.meta.arg.siteId][
          action.meta.arg.resourceId
        ] = "idle";
        state.sessionLoadingState = SessionCallStates.Done;
      });
  },
});

export const { saveSessionResponse, resetSession, setSessionLoadingState } =
  consoleSlice.actions;

export const selSession = (state: AppState) => {
  const urlParts = findUrlParts(window.location.href.split("/"), [
    "sites",
    "console",
  ]);
  if (state.console.sessionDir.hasOwnProperty(urlParts[0])) {
    if (state.console.sessionDir[urlParts[0]].hasOwnProperty(urlParts[1])) {
      return state.console.sessionDir[urlParts[0]][urlParts[1]];
    }
  }
  return {};
};
export const selSessionStatus = (state: AppState) => {
  const urlParts = findUrlParts(window.location.href.split("/"), [
    "sites",
    "console",
  ]);
  if (state.console.sessionStatusDir.hasOwnProperty(urlParts[0])) {
    if (
      state.console.sessionStatusDir[urlParts[0]].hasOwnProperty(urlParts[1])
    ) {
      return state.console.sessionStatusDir[urlParts[0]][urlParts[1]];
    }
  }
  return null;
};
export const selFeatures = (state: AppState) => {
  const urlParts = findUrlParts(window.location.href.split("/"), [
    "sites",
    "console",
  ]);
  return state.console.sessionDir[urlParts[0]][urlParts[1]].features ?? [];
};
export const selGrants = (state: AppState) => {
  const urlParts = findUrlParts(window.location.href.split("/"), [
    "sites",
    "console",
  ]);
  return state.console.sessionDir[urlParts[0]][urlParts[1]].permissions ?? {};
};

export default consoleSlice.reducer;
